import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { afterPageSettles } from '../../lib/deferred';

export type CurrencyCode = 'EUR' | 'USD' | 'MAD';

interface Currency {
  code: CurrencyCode;
  symbol: string;
  name: string;
}

// Rates are relative to MAD (1 MAD = X units of currency).
// Used as fallback when the live fetch fails or is still loading.
// Last manually verified: March 2026.
const FALLBACK_RATES: Record<CurrencyCode, number> = {
  EUR: 0.0917, // 1 MAD ~= 0.0917 EUR
  USD: 0.10, // 1 MAD ~= 0.10 USD
  MAD: 1,
};

export const CURRENCIES: Currency[] = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'MAD', symbol: 'DH', name: 'Moroccan Dirham' },
];

const RATE_CACHE_KEY = 'currency_rates_cache';
const RATE_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

interface RateCache {
  eur: number;
  usd: number;
  timestamp: number;
}

interface LegacyRateCache {
  usd: number;
  mad: number;
  timestamp: number;
}

// Fetches latest MAD-based EUR and USD rates from a public currency endpoint.
async function fetchLiveRates(): Promise<{ eur: number; usd: number } | null> {
  try {
    const res = await fetch(
      'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.json'
    );
    if (!res.ok) throw new Error('HTTP error');
    const data = await res.json();
    const usdPerEur = data?.eur?.usd;
    const madPerEur = data?.eur?.mad;

    if (typeof usdPerEur === 'number' && typeof madPerEur === 'number' && madPerEur > 0) {
      // Convert EUR-based quotes to MAD-based rates.
      const eur = 1 / madPerEur;
      const usd = usdPerEur / madPerEur;
      return { eur, usd };
    }
    return null;
  } catch {
    return null;
  }
}

export interface FixedPrices {
  MAD: number;
  EUR: number;
  USD: number;
}

interface CurrencyContextType {
  currency: Currency;
  rates: Record<CurrencyCode, number>;
  setCurrency: (code: CurrencyCode) => void;
  formatPrice: (amountInMad: number) => string;
  formatFixedPrice: (prices: FixedPrices) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Provides currency selection, conversion rates, and price formatting helpers.
export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>(() => {
    try {
      const saved = localStorage.getItem('currency') as CurrencyCode | null;
      return saved && CURRENCIES.some((c) => c.code === saved) ? saved : 'EUR';
    } catch {
      return 'EUR';
    }
  });

  const [rates, setRates] = useState<Record<CurrencyCode, number>>(FALLBACK_RATES);

  useEffect(() => {
    // Load cached rates if still fresh
    try {
      const cached = localStorage.getItem(RATE_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as Partial<RateCache & LegacyRateCache>;
        if (Date.now() - (parsed.timestamp ?? 0) < RATE_CACHE_TTL_MS) {
          if (typeof parsed.eur === 'number' && typeof parsed.usd === 'number') {
            setRates({ EUR: parsed.eur, USD: parsed.usd, MAD: 1 });
            return;
          }

          if (typeof parsed.usd === 'number' && typeof parsed.mad === 'number' && parsed.mad > 0) {
            setRates({
              EUR: 1 / parsed.mad,
              USD: parsed.usd / parsed.mad,
              MAD: 1,
            });
            return;
          }
        }
      }
    } catch {
      // ignore cache read errors
    }

    // Fetch fresh rates from free public API (no key required, updated daily).
    // FALLBACK_RATES cover first render, so this waits until the page settles.
    return afterPageSettles(() => {
      fetchLiveRates().then((fresh) => {
        if (fresh) {
          const newRates: Record<CurrencyCode, number> = {
            EUR: fresh.eur,
            USD: fresh.usd,
            MAD: 1,
          };
          setRates(newRates);
          try {
            const cache: RateCache = { eur: fresh.eur, usd: fresh.usd, timestamp: Date.now() };
            localStorage.setItem(RATE_CACHE_KEY, JSON.stringify(cache));
          } catch {
            // ignore localStorage write errors
          }
        }
      });
    });
  }, []);

  const currency = CURRENCIES.find((c) => c.code === currencyCode)!;

  const setCurrency = (code: CurrencyCode) => {
    try {
      localStorage.setItem('currency', code);
    } catch {
      // Keep the selected currency for this page even when storage is blocked.
    }
    setCurrencyCode(code);
  };

  const formatPrice = (amountInMad: number): string => {
    const rate = rates[currencyCode];
    const converted = parseFloat((amountInMad * rate).toFixed(2));
    const rounded = Math.round(converted);
    if (currencyCode === 'MAD') {
      return `${rounded} ${currency.symbol}`;
    }
    return `${currency.symbol}${rounded}`;
  };

  const formatFixedPrice = (prices: FixedPrices): string => {
    const price = prices[currencyCode];
    const rounded = Math.round(price);
    if (currencyCode === 'MAD') {
      return `${rounded} ${currency.symbol}`;
    }
    return `${currency.symbol}${rounded}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, rates, setCurrency, formatPrice, formatFixedPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
}

// Returns currency context and ensures it is used under CurrencyProvider.
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
